import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { ReportDateRangePicker } from './date-range/ReportDateRangePicker';
import {
  businessTodayAsLocalDate,
  makePreset,
  type ReportPeriod
} from './date-range/reportPeriod';
import { getLoanTypeCategory, formatCurrency } from './components/fundedSummaryModel';
import { PipelineLoan, getBorrowerName, getLoanOfficerName } from './types';
import { getStrictFundedDate } from './utils';
import { fetchCanonicalReportData } from './reporting';
import {
  buildLOScoreboard,
  resolveCreditedLo,
  emptyAttribution,
  type AttributionContext
} from './loScoreboard';
import { supabase } from '@/utils/supabase';
import { Trophy, RefreshCw, Download, AlertTriangle } from 'lucide-react';
import * as ExcelJS from 'exceljs';
import { createComponentLogger } from '@/utils/logger';
import { parseISO, format } from 'date-fns';
import { buildScoreboardAttribution, type LOScoreboardAttributionRow } from '../../../supabase/functions/_shared/lo-scoreboard';

const log = createComponentLogger('LOScoreboardReport', 'reports');

export function LOScoreboardReport() {
  const [loans, setLoans] = useState<PipelineLoan[]>([]);
  const [attribution, setAttribution] = useState<AttributionContext>(() => emptyAttribution());
  // True when the recon/users attribution data failed to load — in that case the
  // scoreboard would silently fall back to the unreliable Encompass names, so we
  // block the report rather than show known-wrong production credit.
  const [attributionError, setAttributionError] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [dataWarnings, setDataWarnings] = useState<string[]>([]);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState<ReportPeriod>(() => (
    makePreset('ytd', businessTodayAsLocalDate())
  ));

  const fetchData = async () => {
    const today = businessTodayAsLocalDate();
    setIsLoading(true);
    setAttributionError(false);
    setDataError(null);
    setDataWarnings([]);
    setPeriod((current) => (
      current.mode === 'mtd' || current.mode === 'qtd' || current.mode === 'ytd'
        ? makePreset(current.mode, today)
        : current
    ));
    try {
      const reportData = await fetchCanonicalReportData();
      if (reportData.loanError || reportData.payoutError) {
        const message = reportData.loanError?.message || reportData.payoutError?.message;
        setDataError(`${message || 'Unable to load funded production.'} Refresh to try again.`);
        setLoans([]);
        return;
      }
      const fundedLoans = reportData.loans.filter(
        (loan) => getStrictFundedDate(loan) !== null
      );
      if (reportData.payoutWarnings.length > 0) {
        setDataWarnings([
          `${reportData.payoutWarnings.length} payout match conflict${reportData.payoutWarnings.length === 1 ? '' : 's'} resolved to the earliest fund date.`
        ]);
      }

      // Step 3: minimal attribution feed. The RPC is SECURITY DEFINER and
      // permission-scoped so partner users can resolve recon payees without
      // broad direct reads from the users table.
      const { data: attributionRows, error: attributionRpcError } = await supabase
        .rpc('get_lo_scoreboard_attribution');

      if (attributionRpcError) {
        log.warn('Failed to fetch scoreboard attribution', { error: attributionRpcError });
      }

      const { context, noAttributionUsers, incompleteAttribution, reconCount } = buildScoreboardAttribution(
        fundedLoans, attributionRows as LOScoreboardAttributionRow[] | null
      );
      const attributionFailed = !!attributionRpcError || noAttributionUsers || incompleteAttribution > 0;

      if (noAttributionUsers) {
        log.warn('No scoreboard attribution user rows returned while funded loans exist', {
          fundedLoans: fundedLoans.length
        });
      }

      // Residual signal: recons silently hidden by RLS look the same as a
      // legitimately recon-less org client-side. Warn (don't block) so the
      // degraded-to-Encompass case is at least observable in logs.
      if (fundedLoans.length > 0 && reconCount === 0) {
        log.warn('No banker recon rows visible while funded loans exist — attribution may be degraded', {
          fundedLoans: fundedLoans.length
        });
      }

      setLoans(fundedLoans);
      setAttribution(context);
      setAttributionError(attributionFailed);
      setRefreshedAt(new Date());
      log.info('Fetched funded loans for scoreboard', {
        count: fundedLoans.length,
        reconPayees: context.payeeByEncompassId.size,
        incompleteAttribution,
        attributionFailed
      });
    } catch (error) {
      setDataError('Unable to load funded production and attribution. Refresh to try again.');
      log.error('Error fetching scoreboard data', {}, error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter loans by funded date within the selected period
  const filteredLoans = useMemo(() => {
    return loans.filter((loan) => {
      const { from, to } = period;
      const fundedDate = getStrictFundedDate(loan);
      if (from && fundedDate) {
        const fromKey = format(from, 'yyyy-MM-dd');
        const toKey = to ? format(to, 'yyyy-MM-dd') : fromKey;
        if (fundedDate < fromKey || fundedDate > toKey) {
          return false;
        }
      }
      return true;
    });
  }, [loans, period]);

  const rows = useMemo(
    () => buildLOScoreboard(filteredLoans, attribution),
    [filteredLoans, attribution]
  );

  const totals = useMemo(
    () => ({
      loanCount: rows.reduce((s, r) => s + r.loans, 0),
      volume: rows.reduce((s, r) => s + r.volume, 0)
    }),
    [rows]
  );

  const handleExport = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'LendWise';
      workbook.created = new Date();

      // Styling helpers (mirrors FundedLoanReport)
      const headerFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF333333' } };
      const headerFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      const boldFont: Partial<ExcelJS.Font> = { bold: true, size: 10 };
      const normalFont: Partial<ExcelJS.Font> = { size: 10 };
      const thinBorder: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: 'FF000000' } };
      const lightBorder: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: 'FFD0D0D0' } };

      const applyHeaderStyle = (row: ExcelJS.Row, colCount: number) => {
        for (let c = 1; c <= colCount; c++) {
          const cell = row.getCell(c);
          cell.font = headerFont;
          cell.fill = headerFill;
        }
      };

      // --- Scoreboard Sheet ---
      const sheet = workbook.addWorksheet('Scoreboard');
      const colWidths = [5, 26, 9, 16, 32, 12];
      colWidths.forEach((w, i) => {
        sheet.getColumn(i + 1).width = w;
      });

      const companyRow = sheet.addRow(['Lendwise Mortgage Inc.']);
      companyRow.font = { bold: true, size: 14 };
      sheet.mergeCells('A1:F1');

      const titleRow = sheet.addRow(['LO Scoreboard']);
      titleRow.font = { bold: true, size: 12 };
      sheet.mergeCells('A2:F2');

      const periodRow = sheet.addRow([
        `${format(period.from, 'MM/dd/yyyy')} – ${format(period.to, 'MM/dd/yyyy')}`
      ]);
      periodRow.font = { size: 10, italic: true };
      sheet.mergeCells('A3:F3');

      sheet.addRow([]); // blank separator

      const headerRow = sheet.addRow(['#', 'Loan Officer', 'Loans', 'Volume', 'Loan Mix', 'Joined']);
      applyHeaderStyle(headerRow, 6);

      rows.forEach((r) => {
        const dataRow = sheet.addRow([
          r.rank,
          r.name,
          r.loans,
          r.volume,
          r.mix,
          r.joined ? format(parseISO(r.joined), 'MM/dd/yyyy') : ''
        ]);
        dataRow.font = normalFont;
        dataRow.getCell(1).alignment = { horizontal: 'center' };
        dataRow.getCell(3).alignment = { horizontal: 'center' };
        dataRow.getCell(4).numFmt = '#,##0';
      });

      const totalRow = sheet.addRow(['', 'TOTAL', totals.loanCount, totals.volume, '', '']);
      totalRow.font = boldFont;
      totalRow.getCell(3).alignment = { horizontal: 'center' };
      totalRow.getCell(4).numFmt = '#,##0';

      // Box borders from header through TOTAL
      for (let r = headerRow.number; r <= totalRow.number; r++) {
        for (let c = 1; c <= 6; c++) {
          sheet.getCell(r, c).border = {
            top: r === headerRow.number ? thinBorder : lightBorder,
            bottom: r === totalRow.number ? thinBorder : lightBorder,
            left: c === 1 ? thinBorder : lightBorder,
            right: c === 6 ? thinBorder : lightBorder
          };
        }
      }

      // --- Details Sheet (audit trail: credited LO + how it was attributed) ---
      const details = workbook.addWorksheet('Details');
      const detailHeaders = [
        'Loan #',
        'Borrower',
        'Credited LO',
        'Credited Via',
        'Encompass LO',
        'Amount',
        'Loan Type',
        'Channel',
        'Funded Date'
      ];
      const detailHeaderRow = details.addRow(detailHeaders);
      applyHeaderStyle(detailHeaderRow, detailHeaders.length);

      const detailWidths = [14, 24, 22, 18, 22, 16, 30, 18, 14];
      detailWidths.forEach((w, i) => {
        details.getColumn(i + 1).width = w;
      });

      // One row per funded loan, grouped by credited LO then newest funded first.
      const detailRows = filteredLoans
        .map((loan) => ({ loan, credit: resolveCreditedLo(loan, attribution) }))
        .sort((a, b) => {
          if (a.credit.name !== b.credit.name) {
            return a.credit.name.localeCompare(b.credit.name);
          }
          const ad = getStrictFundedDate(a.loan) || '';
          const bd = getStrictFundedDate(b.loan) || '';
          return bd.localeCompare(ad);
        });

      detailRows.forEach(({ loan, credit }) => {
        const fundedDate = getStrictFundedDate(loan);
        const creditedVia = credit.source === 'recon' ? 'Banker recon' : 'Encompass';
        const row = details.addRow([
          loan.trans_details_loan_number || '',
          getBorrowerName(loan),
          credit.name,
          creditedVia,
          getLoanOfficerName(loan),
          loan.trans_details_loan_amt || 0,
          getLoanTypeCategory(loan),
          loan.channel || '',
          fundedDate ? format(parseISO(fundedDate), 'MM/dd/yyyy') : ''
        ]);
        row.font = normalFont;
      });
      details.getColumn(6).numFmt = '#,##0';

      // Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `LO_Scoreboard_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      log.info('Exported LO scoreboard', { loCount: rows.length, detailCount: detailRows.length });
    } catch (error) {
      log.error('Failed to export LO scoreboard', {}, error as Error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Trophy className="h-6 w-6 text-primary" />
            LO Scoreboard
          </h1>
          <p className="text-muted-foreground">
            Loan officers ranked by funded volume for the selected period. Credited to the finalized
            banker-recon payee where available, otherwise the Encompass loan officer. Joined = first
            seen in Lendwise.
          </p>
          {refreshedAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              Refreshed {format(refreshedAt, 'MMM d, yyyy h:mm a')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isLoading || rows.length === 0 || attributionError || Boolean(dataError)}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchData}
            disabled={isLoading}
            aria-label="Refresh LO scoreboard"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {dataWarnings.map((warning) => (
        <div key={warning} role="status" className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
          {warning}
        </div>
      ))}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <ReportDateRangePicker
          value={period}
          onChange={setPeriod}
          max={businessTodayAsLocalDate()}
        />
      </div>

      {/* Leaderboard */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Funded Production by Loan Officer</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : dataError ? (
            <div role="alert" className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
              <p className="font-medium text-destructive">{dataError}</p>
            </div>
          ) : attributionError ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
              <p className="font-medium text-destructive">
                Couldn&apos;t load loan-officer attribution data.
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                This scoreboard credits production using banker-recon payees. That data failed to
                load, so the rankings would be inaccurate. Click Refresh to try again.
              </p>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No funded loans found for the selected period.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[48px] text-center">#</TableHead>
                  <TableHead>Loan Officer</TableHead>
                  <TableHead className="text-center">Loans</TableHead>
                  <TableHead className="text-right">Volume</TableHead>
                  <TableHead>Loan Mix</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.name} className={r.rank === 1 ? 'bg-primary/5' : undefined}>
                    <TableCell className="text-center font-medium">{r.rank}</TableCell>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-center">{r.loans}</TableCell>
                    <TableCell className="text-right">{formatCurrency(r.volume)}</TableCell>
                    <TableCell className="text-muted-foreground">{r.mix}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {r.joined ? format(parseISO(r.joined), 'M/d/yy') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell />
                  <TableCell className="font-bold">TOTAL</TableCell>
                  <TableCell className="text-center font-bold">{totals.loanCount}</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(totals.volume)}</TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default LOScoreboardReport;
